import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import MessagesPanel from "@/components/MessagesPanel";
import NotificationsPanel from "@/components/NotificationsPanel";
import { IconMessage, IconBell } from "@/components/HeaderIcons";

const urlB64ToUint8Array = (b64) => {
  const padding = "=".repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
};

async function ensurePushSubscription(interactive) {
  try {
    if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) return;
    let perm = Notification.permission;
    if (perm === "default" && interactive) perm = await Notification.requestPermission();
    if (perm !== "granted") return;
    const reg = await navigator.serviceWorker.ready;
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      const { data } = await api.get("/push/vapid-public-key");
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlB64ToUint8Array(data.publicKey),
      });
    }
    await api.post("/push/subscribe", { subscription: sub.toJSON() });
  } catch { /* push unsupported or declined */ }
}

const CountBadge = ({ n, testid }) =>
  n > 0 ? (
    <span data-testid={testid}
      className="absolute -top-1.5 -right-1.5 bg-pb-red text-black text-[10px] font-black px-1.5 py-0.5 min-w-[18px] text-center leading-none">
      {n > 99 ? "99+" : n}
    </span>
  ) : null;

export default function HeaderInbox({ variant, onAction, onCounts }) {
  const [counts, setCounts] = useState({ messages: 0, notifications: 0 });
  const [showMessages, setShowMessages] = useState(false);
  const [showNotifs, setShowNotifs] = useState(false);
  const [initialUser, setInitialUser] = useState(null);

  const loadCounts = useCallback(async () => {
    try { const { data } = await api.get("/inbox/counts"); setCounts(data); } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    loadCounts();
    ensurePushSubscription(false);
    const iv = setInterval(loadCounts, 15000);
    const onFocus = () => loadCounts();
    window.addEventListener("focus", onFocus);
    const onOpenMessages = (e) => {
      setInitialUser(e.detail?.id ? { id: e.detail.id, name: e.detail.name, avatar: e.detail.avatar } : null);
      setShowMessages(true);
    };
    window.addEventListener("pb:open-messages", onOpenMessages);
    return () => { clearInterval(iv); window.removeEventListener("focus", onFocus); window.removeEventListener("pb:open-messages", onOpenMessages); };
  }, [loadCounts]);

  useEffect(() => {
    const total = (counts.messages || 0) + (counts.requests || 0) + (counts.notifications || 0);
    onCounts?.(total);
    if ("setAppBadge" in navigator) {
      (total > 0 ? navigator.setAppBadge(total) : navigator.clearAppBadge()).catch(() => {});
    }
  }, [counts, onCounts]);

  return (
    <>
      {variant === "menu" ? (
        <>
          <button data-testid="header-messages-btn" onClick={() => { setShowMessages(true); ensurePushSubscription(true); onAction?.(); }} className="hm-item">
            <span className="relative inline-flex">
              <IconMessage size={24} />
              <CountBadge n={(counts.messages || 0) + (counts.requests || 0)} testid="header-messages-badge" />
            </span>
            <span>Messages</span>
          </button>
          <button data-testid="header-notifications-btn" onClick={() => { setShowNotifs(true); ensurePushSubscription(true); onAction?.(); }} className="hm-item">
            <span className="relative inline-flex">
              <IconBell size={24} />
              <CountBadge n={counts.notifications} testid="header-notifications-badge" />
            </span>
            <span>Notifications</span>
          </button>
        </>
      ) : (
      <>
      <button data-testid="header-messages-btn" onClick={() => { setShowMessages(true); ensurePushSubscription(true); }} title="Messages"
        className="hdr-icon relative w-9 h-9 flex items-center justify-center">
        <IconMessage size={26} />
        <CountBadge n={(counts.messages || 0) + (counts.requests || 0)} testid="header-messages-badge" />
      </button>
      <button data-testid="header-notifications-btn" onClick={() => { setShowNotifs(true); ensurePushSubscription(true); }} title="Notifications"
        className="hdr-icon relative w-9 h-9 flex items-center justify-center">
        <IconBell size={26} />
        <CountBadge n={counts.notifications} testid="header-notifications-badge" />
      </button>
      </>
      )}
      <MessagesPanel open={showMessages} onClose={() => { setShowMessages(false); setInitialUser(null); }} onRead={loadCounts} initialUser={initialUser} />
      <NotificationsPanel open={showNotifs} onClose={() => setShowNotifs(false)} onRead={loadCounts}
        onOpenMessages={() => { setShowNotifs(false); setShowMessages(true); }} />
    </>
  );
}
